import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DevicePhoneMobileIcon, ComputerDesktopIcon, TrashIcon } from '@heroicons/react/24/outline';


const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || `\${API_BASE_URL}`;

interface TrustedDevice {
  id: string;
  name: string;
  deviceType: 'mobile' | 'desktop';
  lastAccessed: string;
  ipAddress: string;
  location: string;
  isCurrent: boolean;
}

const TrustedDevicesPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [devices, setDevices] = useState<TrustedDevice[]>([]);
  const [revoking, setRevoking] = useState<string | null>(null);

  useEffect(() => {
    fetchDevices();
  }, []);

  const fetchDevices = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`\${API_BASE_URL}/api/settings/trusted-devices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setDevices(data.devices || []);
      }
    } catch (error) {
      console.error('Failed to load trusted devices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (deviceId: string) => {
    if (!confirm('Are you sure you want to revoke access for this device?')) {
      return;
    }
    setRevoking(deviceId);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_BASE_URL}/api/settings/trusted-devices/${deviceId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        await fetchDevices();
      }
    } catch (error) {
      console.error('Failed to revoke device:', error);
    } finally {
      setRevoking(null);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate('/dashboard/settings/security')}
        className="text-sm text-indigo-600 hover:text-indigo-500 mb-4"
      >
        ← Back to Security Settings
      </button>
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Trusted Devices</h1>
      <p className="mb-6 text-sm text-gray-600">
        Manage devices that have access to your account
      </p>
      {devices.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <DevicePhoneMobileIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No trusted devices</h3>
          <p className="text-sm text-gray-600">Devices you log in to will appear here</p>
        </div>
      ) : (
        <div className="space-y-4">
          {devices.map((device) => (
            <div
              key={device.id}
              className={`bg-white rounded-lg shadow-sm border-2 p-6 ${
                device.isCurrent ? 'border-indigo-200' : 'border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 p-2 bg-gray-100 rounded-lg">
                    {device.deviceType === 'mobile' ? (
                      <DevicePhoneMobileIcon className="w-6 h-6 text-gray-600" />
                    ) : (
                      <ComputerDesktopIcon className="w-6 h-6 text-gray-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="text-lg font-medium text-gray-900">{device.name}</h3>
                      {device.isCurrent && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                          Current Device
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 space-y-0.5">
                      <div>Last accessed: {new Date(device.lastAccessed).toLocaleString()}</div>
                      <div>Location: {device.location}</div>
                      <div>IP: {device.ipAddress}</div>
                    </div>
                  </div>
                </div>
                {!device.isCurrent && (
                  <button
                    onClick={() => handleRevoke(device.id)}
                    disabled={revoking === device.id}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-md disabled:opacity-50"
                    title="Revoke access"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TrustedDevicesPage;
